import React from 'react';
import { Link } from 'react-router-dom';
import { Coins } from '@phosphor-icons/react';
import type { TenantBalance } from './useTenantBalances';

interface Props {
  /** Openstand van deze huurder; `undefined` = geen incassodossier. */
  balance?: TenantBalance;
  /** Naam van de huurder, voor de tooltip. */
  name?: string;
  /** `compact` past in een tabelcel; `full` staat op een detailpagina. */
  variant?: 'compact' | 'full';
  className?: string;
}

const formatAmount = (value: number) =>
  new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);

/**
 * De **openstand** van een huurder: wat er in de boekhouding nog openstaat.
 *
 * Eén herbruikbaar badge, zodat elke plek waar een huurder getoond wordt dezelfde
 * weergave krijgt (helpdesk, renovatietickets, …) in plaats van per module een eigen
 * variant. De cijfers komen uit {@link useTenantBalances}.
 *
 * Kleur naar ernst: openstaand maar niet te laat = amber, meer dan 30 dagen te laat =
 * rood. Zonder openstand wordt er niets getoond — een groene "€ 0" badge bij elke huurder
 * zou alleen ruis zijn.
 *
 * Klikken opent het incassodossier, want dat is waar je iets aan die openstand doet.
 */
export const TenantBalanceBadge: React.FC<Props> = ({
  balance,
  name,
  variant = 'compact',
  className = '',
}) => {
  if (!balance || balance.totalDebt <= 0) return null;

  const isLate = balance.maxDaysOverdue > 30;
  const kleuren = isLate
    ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
    : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100';

  const tooltip = [
    name ? `${name}: ` : '',
    `${formatAmount(balance.totalDebt)} openstaand`,
    ` · ${balance.invoiceCount} factu${balance.invoiceCount === 1 ? 'ur' : 'ren'}`,
    balance.maxDaysOverdue > 0 ? ` · tot ${balance.maxDaysOverdue} dagen te laat` : '',
    ` · incassofase: ${balance.phase}`,
  ].join('');

  return (
    <Link
      to={`/rental/incasso/dossier/${balance.incassoTicketId}`}
      onClick={(e) => e.stopPropagation()}
      title={tooltip}
      className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 font-medium transition-colors ${kleuren} ${
        variant === 'compact' ? 'text-[10px]' : 'text-xs'
      } ${className}`}
    >
      <Coins size={variant === 'compact' ? 10 : 12} weight="duotone" />
      {variant === 'compact' ? (
        formatAmount(balance.totalDebt)
      ) : (
        <>
          Openstand {formatAmount(balance.totalDebt)}
          <span className="font-normal">
            ({balance.invoiceCount} factu{balance.invoiceCount === 1 ? 'ur' : 'ren'}
            {balance.maxDaysOverdue > 0 ? `, ${balance.maxDaysOverdue} d te laat` : ''})
          </span>
        </>
      )}
    </Link>
  );
};

export default TenantBalanceBadge;
